import React from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from '../components/redesign/StatusBadge';
import PriorityChip from '../components/redesign/PriorityChip';
import { EntityChip } from '../components/redesign/EntityChip';
import { ArrowsClockwise, Tray, Users, CheckCircle } from '@phosphor-icons/react';
import { COMPLAINT_GROUP_LABEL } from '../constants/ticketOptions';

const TEAM_SUBHEAD = "Your reports' open tickets. Read-only.";

// "3h" / "2d 5h" from age in hours (ported verbatim from live).
function fmtAge(hours) {
  if (hours == null) return '—';
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

// Colour the Age string by staleness: default → amber (>48h) → red (≥1 week).
function ageToneClass(hours) {
  if (hours == null) return 'text-ink-faint';
  if (hours >= 168) return 'text-status-escalated';        // 1 week+
  if (hours >= 48)  return 'text-status-pending-finance';  // 48h+
  return 'text-ink-faint';
}

// Column header cell.
const Th = ({ children, extra = '' }) => (
  <th className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint first:px-5 ${extra}`}>{children}</th>
);

// One row. Whole row is clickable → onRowClick(id).
function TicketRow({ t, showAssignee, onRowClick }) {
  return (
    <tr onClick={() => onRowClick(t.id)}
        className="cursor-pointer border-b border-paper-border hover:bg-paper-muted/50">
      <td className="px-5 py-3">
        <div className="text-sm font-semibold text-status-under-process">{t.ticket_number}</div>
        <div className="mt-0.5 max-w-[16rem] truncate text-xs text-ink-faint" dir="auto">{t.subject || '—'}</div>
      </td>
      {showAssignee && (
        <td className="px-4 py-3 text-sm text-ink">{t.assigned_to_name || 'Unassigned'}</td>
      )}
      <td className="px-4 py-3 text-sm text-ink-muted" dir="auto">
        {t.customer_name || t.customer_mobile || '—'}
      </td>
      <td className="px-4 py-3 text-xs text-ink-muted">
        {COMPLAINT_GROUP_LABEL[t.complaint_group] || t.complaint_group || '—'}
      </td>
      <td className="px-4 py-3">
        {t.entity_slug ? <EntityChip entitySlug={t.entity_slug} /> : <span className="text-xs text-ink-faint">—</span>}
      </td>
      <td className="px-4 py-3"><PriorityChip priority={t.complaint_priority} /></td>
      <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
      <td className={`px-4 py-3 whitespace-nowrap text-xs ${ageToneClass(t.age_hours)}`} title={t.created_at}>
        {fmtAge(t.age_hours)}
      </td>
    </tr>
  );
}

// Positive empty-state for the Actionable section. Team section is hidden
// entirely when empty (owned by container), so no team-empty markup here.
function EmptyActionable() {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <CheckCircle size={28} weight="fill" className="text-status-resolved" />
      <div className="text-sm font-semibold text-ink">You're all caught up.</div>
      <div className="text-xs text-ink-faint">No tickets are waiting on you right now.</div>
    </div>
  );
}

function SectionTable({ rows, showAssignee, onRowClick }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-paper-border bg-paper-muted/40">
            <Th>Ticket</Th>
            {showAssignee && <Th>Assignee</Th>}
            <Th>Customer</Th>
            <Th>Group</Th>
            <Th>Entity</Th>
            <Th>Priority</Th>
            <Th>Status</Th>
            <Th>Age</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <TicketRow key={t.id} t={t} showAssignee={showAssignee} onRowClick={onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ icon, title, count, subhead, extraPill, empty, children }) {
  return (
    <section className="overflow-hidden rounded-card border border-paper-border bg-paper-panel shadow-soft">
      <div className="border-b border-paper-border bg-paper-muted/40 px-5 py-4">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-serif text-base text-ink">{title}{count != null ? ` (${count})` : ''}</h3>
          {extraPill}
        </div>
        {subhead && <p className="mt-1 text-[13px] text-ink-muted">{subhead}</p>}
      </div>
      {empty ?? children}
    </section>
  );
}

export default function PendingActionView({
  actionable = [],
  team = [],
  actionableSubhead = '',
  teamSubhead = TEAM_SUBHEAD,
  showClosedLink = false,
  loading = false,
  onReload,
  onRowClick,
}) {
  return (
    <div className="p-1">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl text-ink">Pending Action</h1>
            <p className="mt-0.5 text-[13px] text-ink-muted">
              Tickets waiting on you — nothing moves until you act.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {showClosedLink && (
              <Link
                to="/tickets?status=resolved,closed"
                className="text-[13px] text-ink-muted hover:text-ink hover:underline"
              >
                View closed &amp; resolved →
              </Link>
            )}
            <button
              onClick={onReload}
              title="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
              <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <ArrowsClockwise size={28} className="animate-spin text-status-under-process" />
          </div>
        ) : (
          <>
            {/* Actionable — always rendered */}
            <Section
              icon={<Tray size={16} className="text-status-under-process" />}
              title="Needs My Action"
              count={actionable.length}
              subhead={actionableSubhead}
              empty={actionable.length === 0 ? <EmptyActionable /> : null}>
              <SectionTable rows={actionable} showAssignee={false} onRowClick={onRowClick} />
            </Section>

            {/* Team — hidden entirely when empty (preserves live behaviour) */}
            {team.length > 0 && (
              <Section
                icon={<Users size={16} className="text-status-escalated" />}
                title="Team — Awaiting Action"
                count={team.length}
                subhead={teamSubhead}
                extraPill={
                  <span className="rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] text-ink-muted">
                    read-only
                  </span>
                }>
                <SectionTable rows={team} showAssignee={true} onRowClick={onRowClick} />
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
